import { Paper, useTheme } from "@mui/material";
import { GridFilterModel, useGridApiRef } from "@mui/x-data-grid-pro";
import { endOfDay, startOfMonth } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  formatNumber,
  IsActionColumnVisibleForTransactionsPage,
  isSurchargeEnabled
} from "../../common/components/AppUtils";
import PageHeader from "../../common/components/PageHeader/PageHeader";
import { RefundPopup } from "../../common/components/RefundPopup";
import { useColumnVisibility } from "../../common/UserPreference/hooks/useColumnsVisibilityHook";
import {
  PaymentTotals,
  SortModel,
  Transaction,
  TransactionRequest,
  TransactionResponseWithTotals
} from "../../domain/transactions/type";
import { areFiltersEqual } from "../../helpers/helpers";
import { getKeyByValue } from "../../mappings/processing-method.mapping";
import {
  useGetPaymentsQuery,
  useLazyGetAllPaymentsToExportQuery,
  useVoidTransactionMutation
} from "../../services/API/transactionsApiSlice";
import { handleApiError } from "../../services/errorTranslator";
import { PaymentStatus } from "../../types/enums";
import { IVoidTransactionRequest } from "../../types/types";
import { exportDataToExcel } from "../../utils/ExportToExcel";
import { convertToUTC } from "../../utils/Util";
import {
  GetCompanyDealerId,
  GetDealerPlatformFeeOption
} from "../../utils/UtilTarget";
import DataGridProComponent from "../PerseusPay/Reports/DataGridProComponent";
import TransactionAuditTrail from "../Transaction/transaction.audittrail";
import { useTransactionsColumns } from "./useTransactionsColumns";
import { useGetMerchantCustomFieldsQuery } from "../../services/API/customFieldApiSlice";
import { sanitizeOperator } from "../../mappings/mui-grid-operators-mapping";

/**
 * TransactionsGrid Component
 *
 * Renders a server-side paginated DataGrid with support for:
 * - Dealer location filtering
 * - Date range filtering
 * - Search input
 * - Sorting
 * - Refund popup
 * - Transaction audit trail drawer
 *
 * Integrates with RTK Query hooks to fetch transaction data and handle void/refund requests.
 */
export const TransactionsGrid = () => {

    const theme = useTheme();
    const pageKey = 'transactions';

  const isActionColumnVisible = IsActionColumnVisibleForTransactionsPage();

  // Define the default config — always enforced
  const defaultColumns = {
    surcharge: isSurchargeEnabled(),
    platformFee: GetDealerPlatformFeeOption(),
    totalAmount: isSurchargeEnabled() || GetDealerPlatformFeeOption(),
    action: isActionColumnVisible
  };

  /**
   * Popup state for initiating refunds.
   */
  const [refundPopup, setRefundPopup] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    row?: Transaction;
  }>({ open: false, anchorEl: null });

  const drawerRef = useRef<any>();

  // Default date range = start of current month to end of today
  const today = new Date();
  const defaultDateRange: Date[] = [startOfMonth(today), endOfDay(today)];

  /** Date range selected in the filter */
  const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date]>([
    defaultDateRange[0],
    defaultDateRange[1]
  ]);

  /** Pagination model for DataGrid */
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100
  });

  /** Sorting state */
  const [sortModel, setSortModel] = useState<SortModel>({
    field: "dateCreated",
    sort: "desc"
  });

  /** Loading and search states */
  const [isLoading, setIsLoading] = useState(true);
  const [
    transactions,
    setTransactions
  ] = useState<TransactionResponseWithTotals | null>(null);
  const [
    transactionsTotal,
    setTransactionsTotal
  ] = useState<PaymentTotals | null>(null);
  const [dealerLocationId, setDealerLocationId] = useState<number>(-1);
  const [company_Dealer_Id, setcompanyDealerId] = useState<number>(0);
  const [searchCriteria, setSearchCriteria] = useState<string>("");
  const [isSearchReady, setIsSearchReady] = useState(true);
  const [isExportButtonClicked, setIsExportButtonClicked] = useState<boolean>(
    false
  );
  const apiRef = useGridApiRef();
  const merchantId = localStorage.getItem("merchantId") || "";

  /** DataGrid filter model */
  const [logicOperator, setLogicOperator] = useState<"and" | "or">("and");
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: []
  });

  /** Controls visibility of surcharge, platformFee, action columns */
  const { columnVisibilityModel, handleVisibilityChange } = useColumnVisibility(
    pageKey,
    defaultColumns
  );

  /** Load companyDealerId for API requests */
  useEffect(() => {
    const getCompanyDealerId = async () => {
      var companyDealerId = await GetCompanyDealerId();
      setcompanyDealerId(Number(companyDealerId ?? 0));
    };
    getCompanyDealerId();
  }, []);

  /**
   * Fetch merchant custom field definitions.
   */
  const { data: merchantCustomFields } = useGetMerchantCustomFieldsQuery(
    merchantId,
    {
      skip: !merchantId,
      refetchOnMountOrArgChange: true
    }
  );

  /**
   * Fetch transactions via RTK Query.
   * Triggers refetch when pagination, search, filters, or sorting changes.
   */
  const {
    data: transactionsResponse,
    isFetching,
    error,
    refetch,
    isUninitialized
  } = useGetPaymentsQuery(
    {
      dealerId: dealerLocationId === -1 ? 0 : dealerLocationId,
      companyDealerId: dealerLocationId === -1 ? company_Dealer_Id : 0,
      Page: paginationModel.page + 1,
      PageSize: paginationModel.pageSize,
      fromDate: convertToUTC(selectedDateRange[0]).trim(),
      toDate: convertToUTC(selectedDateRange[1], "end").trim(),
      searchCriteria: searchCriteria,
      sortField: sortModel.field,
      sortDirection: sortModel.sort,
      filters: filterModel.items.map(f => ({
        field: f.columnField,
        operator: sanitizeOperator(f.operatorValue),
        value: f.value
      })),
      logicOperator: filterModel.linkOperator
    },
    {
      skip: company_Dealer_Id <= 0 || !isSearchReady,
      refetchOnMountOrArgChange: true
    }
  );

  /** Manage loading state and assign transactions when response is available */
  useEffect(() => {
    if (isFetching) {
      setIsLoading(true);
    } else {
      try {
        if (
          transactionsResponse &&
          transactionsResponse.pagedList &&
          transactionsResponse.pagedList.data.length >= 0
        ) {
          setTransactions(transactionsResponse);
          setTransactionsTotal(
            transactionsResponse.totals ? transactionsResponse.totals : null
          );
          stopSearch();
        }
        if (error) {
          stopSearch();
          handleApiError(error);
        }
      } catch (ex) {
        stopSearch();
        handleApiError(ex);
      }
    }
  }, [transactionsResponse, isFetching]);

  /**
   * Get custom field column definitions from merchant settings.
   * Sorted by field name for consistent ordering.
   */
  const customFieldLabels = useMemo(() => {
    if (!merchantCustomFields || !Array.isArray(merchantCustomFields))
      return [];
    return [...merchantCustomFields]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(field => ({
        name: field.name,
        label: field.label,
        direction: field.direction
      }));
  }, [merchantCustomFields]);

  /**
   * Prepare DataGrid rows with additional computed fields.
   * Flattens custom field values onto each row for direct grid access.
   */
  const allRows = useMemo(() => {
    return transactions &&
      transactions.pagedList &&
      transactions.pagedList?.data.length > 0
      ? transactions.pagedList.data.map((item, index) => {
          const itemAny = item as any;
          const row: any = {
            id: `${index}`,
            ...item,
            surcharge: item.platformFeeTypeId === 1 ? item.platformFee : 0,
            platformFee: item.platformFeeTypeId === 0 ? item.platformFee : 0,
            status:
              item.status === "CardSaved"
                ? PaymentStatus.CardSaved
                : item.status === "PendingRefund"
                ? PaymentStatus.PendingRefund
                : item.status === "RefundFailed"
                ? PaymentStatus.RefundFailed
                : item.status === "Reverted"
                ? PaymentStatus.AutoReversal
                : item.status
          };
          // Flatten custom field values onto the row
          customFieldLabels.forEach(cf => {
            const fieldKey = `${cf.name}::${cf.label}`;
            const match = itemAny.customFields?.find(
              (f: any) => f.name === cf.name
            );
            row[fieldKey] = match?.value ?? "";
          });
          return row;
        })
      : [];
  }, [transactions, customFieldLabels]);

  /**
   * Compute totals row for pinned footer.
   */
  const totalsRow = useMemo(() => {
    if (!allRows.length) return { id: "totals" };

    const amount =
      transactionsTotal.totalRequestedAmount +
      transactionsTotal.totalRefundedRequestedAmount;
    const surcharge =
      transactionsTotal.totalSurcharge +
      transactionsTotal.totalRefundedSurcharge;
    const platformFee =
      transactionsTotal.totalPlatformFee +
      transactionsTotal.totalRefundedPlatformFee;
    const total =
      transactionsTotal.totalAmount + transactionsTotal.totalRefundedAmount;

    return {
      id: "totals",
      invoiceId: "Totals",
      paymentId: "",
      dealerLocation: "",
      customerName: "",
      transactionAmount: formatNumber(amount, true),
      surcharge: formatNumber(surcharge, true),
      platformFee: formatNumber(platformFee, true),
      totalAmount: formatNumber(total, true),
      status: "",
      paymentType: "",
      maskedCardNumber: "",
      transactionType: ""
    };
  }, [allRows]);

  /** Handle filter changes */
  const handleFilterModelChange = (model: GridFilterModel) => {
    if (
      model.items.length === 0 ||
      (model.items.length > 0 &&
        model.items.every(x => x.value !== undefined) &&
        (!areFiltersEqual(model.items, filterModel.items) ||
          model.linkOperator !== filterModel.linkOperator))
    ) {
      var _model = structuredClone(model);
      // If 'paymentType' exists, split its value by space and keep the first part
      _model.items
        .filter(x => x.columnField === "paymentType")
        .forEach(x => {
          x.value = getKeyByValue(x.value).split(" ")[0];
        });

      _model.items
        .filter(x => x.columnField === "status")
        .forEach(x => {
          x.value = x.value.split(" ").join("");
        });

      setFilterModel(_model);
      setLogicOperator(model.linkOperator);
      makeSearchReady();
    }
  };

  /** make server side paged search ready */
  const makeSearchReady = () => {
    setIsLoading(true);
    setIsSearchReady(true);
  };
  /** close/stop server side paged search    */
  const stopSearch = () => {
    setIsLoading(false);
    setIsSearchReady(false);
  };
  /** Handle date range changes */
  const handleDateRangePickerChange = (value: [Date, Date]) => {
    setSelectedDateRange(value);
    makeSearchReady();
  };

  /** Handle dealer location change */
  const handleDealerLocationChange = (value: number) => {
    setDealerLocationId(value);
    makeSearchReady();
  };

  /** Handle the Generic Search Bar change event */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // When search is cleared
    if (value.length === 0 && searchCriteria.length > 0) {
      setSearchCriteria(value);
      makeSearchReady();
      handleRefresh();
      return;
    }

    // Normal typing
    setSearchCriteria(value);
  };

  /** Open refund popup */
  const handleOpenRefundPopup = (
    event: React.MouseEvent<HTMLElement>,
    row: Transaction
  ) => {
    setRefundPopup({ open: true, anchorEl: event.currentTarget, row });
  };

  /** Close refund popup */
  const handleCloseRefundPopup = () => {
    setRefundPopup(prev => ({
      ...prev,
      open: false,
      anchorEl: null,
      row: undefined
    }));
  };

  // 🔹 Refresh handler
  const handleRefresh = async () => {
    if (isUninitialized) {
      return;
    }
    await refetch();
  };

  // Export all data to excel including all the filters
  const [triggerExportPayments] = useLazyGetAllPaymentsToExportQuery();

  /**
   * 🔹 Builds the export payload for API call
   * Includes applied filters, sorting, and date range.
   *
   * @param dealerLocationId - Currently selected dealer location
   * @param company_Dealer_Id - Parent dealer/company ID
   * @param selectedDateRange - The active date range filter
   * @param searchCriteria - Search text or keyword
   * @param sortModel - Current grid sort model
   * @param filterModel - Current grid filter model
   * @returns TransactionRequest - Fully constructed payload for export API
   */
  const buildExportPayload = ({
    dealerLocationId,
    company_Dealer_Id,
    selectedDateRange,
    searchCriteria,
    sortModel,
    filterModel
  }: any): TransactionRequest => ({
    dealerId: dealerLocationId === -1 ? 0 : dealerLocationId,
    companyDealerId: dealerLocationId === -1 ? company_Dealer_Id : 0,
    fromDate: convertToUTC(selectedDateRange[0]).trim(),
    toDate: convertToUTC(selectedDateRange[1], "end").trim(),
    searchCriteria,
    sortField: sortModel.field,
    sortDirection: sortModel.sort,
    Page: 1,
    PageSize: 10000, //max export limit
    filters: filterModel.items.map(f => ({
      field: f.columnField,
      operator: sanitizeOperator(f.operatorValue),
      value: f.value
    })),
    logicOperator: filterModel.linkOperator
  });

  /**
   * 🔹 Returns whether a status indicates refund/cancel
   */
  const isReversedStatus = status =>
    [PaymentStatus.Refunded, PaymentStatus.Cancelled].includes(status);

  /**
   * 🔹 Maps transaction data into export-friendly rows
   * Adds calculated surcharge/platformFee and status formatting.
   * Flattens custom field values onto each row.
   *
   * @param data - Transaction data array
   * @returns Formatted row array for export
   */
  const mapExportRows = (data: any[]): any[] =>
    data.map((item, index) => {
      const isReversed = isReversedStatus(item.status);
      const sign = isReversed ? -1 : 1;

      const row: any = {
        id: String(index),
        ...item,
        surcharge:
          item.platformFeeTypeId === 1
            ? formatNumber(item.platformFee)
            : "0.00",
        platformFee:
          item.platformFeeTypeId === 0
            ? formatNumber(item.platformFee)
            : "0.00",
        status:
          item.status === "CardSaved"
            ? PaymentStatus.CardSaved
            : item.status === "PendingRefund"
            ? PaymentStatus.PendingRefund
            : item.status === "Reverted"
            ? PaymentStatus.AutoReversal
            : item.status,
        transactionAmount: formatNumber(item.transactionAmount),
        totalAmount: formatNumber(item.totalAmount)
      };

      // Flatten custom field values onto the row for export
      customFieldLabels.forEach(cf => {
        const fieldKey = `${cf.name}::${cf.label}`;
        const match = item.customFields?.find((f: any) => f.name === cf.name);
        row[fieldKey] = match?.value ?? "";
      });

      return row;
    });

  /**
   * 🔹 Builds the totals row appended to the export
   * Aggregates total amounts, fees, and surcharges for summary display.
   *
   * @param totals - Object containing all transaction totals
   * @returns Totals row object
   */
  const buildTotalsRow = (totals: any) => ({
    id: "Totals",
    externalTransId: null,
    transactionType: "",
    transactionReference: null,
    transactionAmount: formatNumber(
      totals.totalRequestedAmount + totals.totalRefundedRequestedAmount
    ),
    customerName: null,
    transactionDate: "",
    paymentId: "",
    isCancelled: null,
    invoiceId: "",
    isRefunded: false,
    cancelOrRefund: null,
    dealerLocation: "",
    status: "",
    refundInitiated: false,
    platformFee: formatNumber(
      totals.totalPlatformFee + totals.totalRefundedPlatformFee
    ),
    surcharge: formatNumber(
      totals.totalSurcharge + totals.totalRefundedSurcharge
    ),
    totalAmount: formatNumber(totals.totalAmount + totals.totalRefundedAmount),
    platformFeeTypeId: 0,
    dealerId: 0,
    refundedAmount: 0,
    platformFeePercentage: 0,
    maskedCardNumber: "",
    paymentType: "",
    refundedDate: "",
    transactionTypeId: 0
  });

  /**
   * 🔹 Handles export to Excel action
   * Builds the request payload, fetches all data, formats it,
   * and triggers export including visible columns & totals.
   */
  const handleExportToExcel = async () => {
    try {
      const totalRows = transactions?.pagedList?.rowCount ?? 0;

      // ✅ Validate record count
      if (totalRows === 0 || apiRef.current.getVisibleColumns().length === 0) {
        toast.info("No results to export.");
        return;
      }
      if (totalRows > 10000) {
        toast.info("Currently, export supports a maximum of 10,000 records.");
        return;
      }

      setIsExportButtonClicked(true);

      const exportPayload = buildExportPayload({
        dealerLocationId,
        company_Dealer_Id,
        selectedDateRange,
        searchCriteria,
        sortModel,
        filterModel
      });

      const { data: exportData, error } = await triggerExportPayments(
        exportPayload
      );

      // ✅ Handle API errors and empty data
      if (error) return handleApiError(error);
      if (!exportData?.pagedList?.data?.length) {
        toast.info("No results to export.");
        return;
      }

      // ✅ Get visible columns (for consistent UI export)
      const visibleColumns =
        apiRef.current
          ?.getVisibleColumns()
          ?.filter(x => x.field !== "action") || []; // getting all the currently visible columns in the grid.

      const rows = mapExportRows(exportData.pagedList.data);
      rows.push(buildTotalsRow(exportData.totals)); // adding the totals row in the data to be exported.

      // ✅ Trigger export
      exportDataToExcel(rows, visibleColumns, "Transactions"); // calling the service export method to export in Xlsx.

      toast.success(`Exported ${rows.length - 1} records successfully.`);
    } catch (err) {
      toast.error("An error occurred during export. Please try again.");
    } finally {
      setIsExportButtonClicked(false);
    }
  };

  /** Transaction columns configuration (includes custom field columns) */
  const columns = useTransactionsColumns(
    handleOpenRefundPopup,
    customFieldLabels
  );

  /** RTK mutation hook for voiding transactions */
  const [voidTransactionAsync] = useVoidTransactionMutation();

  /**
   * Trigger refund/void transaction API call.
   * Supports both full and partial refunds.
   */
  async function VoidTransaction(
    refundType: "full" | "partial" = "full",
    AmountToRefund: Number
  ) {
    const payload: IVoidTransactionRequest = {
      dealerId: refundPopup.row.dealerId.toString(),
      externalTransId: refundPopup.row.paymentId,
      uniqueTransId: refundPopup.row.paymentId,
      requestedAmount: 0
    };

    if (refundType === "partial") {
      payload.requestedAmount = AmountToRefund;
    }

    try {
      var response = await voidTransactionAsync(payload).unwrap();
      if (
        response &&
        response.transactionResponse &&
        response.transactionResponse.resultMessage === PaymentStatus.Authorised
      ) {
        toast.success(
          `Refund Successfully Initiated for the payment Id: ${payload.uniqueTransId}`
        );
        setIsSearchReady(true);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      handleCloseRefundPopup();
    }
  }

  return (
    <>
      {
        <>
          <PageHeader
            title="Transactions"
            browserTitle={theme.browserTitle}
            showSearch={true}
            onSearchChange={handleSearchChange}
            onSearchEnter={event => {
              if (event.key === "Enter" && searchCriteria.length > 0) {
                makeSearchReady();
                handleRefresh();
              }
            }}
            showDealerFilter={true}
            showDateRange={true}
            searchValue={searchCriteria}
            onDealerLocationChange={handleDealerLocationChange}
            dateRange={[defaultDateRange[0], defaultDateRange[1]]}
            onDateRangePickerChange={handleDateRangePickerChange}
            excelButtonProps={{
              showExcelButton: true,
              isSubmitted: isExportButtonClicked,
              isDisabled:
                (transactions?.pagedList?.rowCount ?? 0) === 0 || isLoading,
              label: "Export",
              title: "Export supports up to 10,000 records.",
              onExportButtonClick: handleExportToExcel
            }}
          />

          <Paper sx={{ position: "relative" }}>
            <DataGridProComponent
              rows={allRows || []}
              columns={columns}
              pinnedTotalRow={totalsRow}
              autoRowHeight={true}
              isLoading={isLoading}
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              rowCount={transactions?.pagedList?.rowCount ?? 0}
              onFilterModelChange={handleFilterModelChange}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleVisibilityChange}
              apiRefProp={apiRef}
              imageNotFound="NoTransaction"
              serverSidePagination={true}
              onRowClick={row => {
                drawerRef.current.openDrawer(row);
              }}
              onPageChange={page => {
                setPaginationModel(prev => ({ ...prev, page }));
                makeSearchReady();
              }}
              onPageSizeChange={pageSize => {
                setPaginationModel(prev => ({ ...prev, pageSize }));
                makeSearchReady();
              }}
              showCustomToolbarColumnsButton={true}
              showGridRefreshButton={true}
              onRefresh={() => {
                makeSearchReady();
                handleRefresh();
              }}
              customSx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal !important", // allow wrapping
                  wordBreak: "break-word !important", // break long words
                  overflowWrap: "anywhere !important",
                  display: "flex !important", // ✅ keep flex for proper alignment
                  alignItems: "center", // vertical center
                  lineHeight: "1.3",
                },
                "& .MuiDataGrid-cell--textRight": {
                  justifyContent: "flex-end !important" // ✅ preserve right alignment for numeric
                },
                "& .MuiDataGrid-row": {
                  fontSize: "0.85rem !important"
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                  visibility: "visible !important",
                  opacity: 1,
                  width: "auto !important",
                  pointerEvents: "auto"
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
                  display: "flex !important",
                  visibility: "visible !important"
                },
                // Prevent header collapsing because of the extra icon
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  maxWidth: "calc(100% - 25px)" // leave space for icon
                }
              }}
            />
          </Paper>

          <TransactionAuditTrail ref={drawerRef} />

          <RefundPopup
            open={refundPopup.open}
            anchorEl={refundPopup.anchorEl}
            onClose={handleCloseRefundPopup}
            onConfirm={(refundType, amount) => {
              VoidTransaction(refundType, amount);
            }}
            maxAmount={refundPopup?.row?.transactionAmount ?? 0}
            refundedAmount={refundPopup?.row?.refundedAmount ?? 0}
            surchargeFeePercentage={
              refundPopup?.row?.platformFeePercentage ?? 0
            }
          />
        </>
      }
    </>
  );
};
